import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Loader2, AlertCircle, Package, MapPin,
  Phone, Mail, Truck, CheckCircle, Clock, Save,
} from 'lucide-react';
import { supabase } from '@/utils/supabase';

function fmtCurrency(cents) {
  return '$' + (cents / 100).toFixed(2);
}

function InfoBlock({ label, children }) {
  return (
    <div className="flex flex-col gap-1">
      <p className="font-['Space_Mono'] text-[0.58rem] tracking-[0.14em] uppercase text-[var(--muted)]">{label}</p>
      <div className="text-sm text-[var(--ink)] font-['Space_Grotesk']">{children}</div>
    </div>
  );
}

const FULFILLMENT_STATES = ['unfulfilled', 'partial', 'fulfilled'];

export default function AdminOrderDetail() {
  const { id }   = useParams();
  const navigate = useNavigate();
  const [order, setOrder]         = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [note, setNote]           = useState('');
  const [tracking, setTracking]   = useState('');
  const [fulfillment, setFulfillment] = useState('unfulfilled');
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    (async () => {
      const { data, error: qErr } = await supabase
        .from('orders')
        .select('*')
        .eq('id', id)
        .single();
      if (qErr) { setError(qErr.message); setLoading(false); return; }
      setOrder(data);
      setNote(data.notes || '');
      setTracking(data.tracking_number || '');
      setFulfillment(data.fulfillment_status || 'unfulfilled');
      setLoading(false);
    })();
  }, [id]);

  const saveChanges = async () => {
    setSaving(true);
    const { error: uErr } = await supabase.from('orders').update({
      notes: note,
      tracking_number: tracking || null,
      fulfillment_status: fulfillment,
    }).eq('id', id);
    if (uErr) setError(uErr.message);
    setSaving(false);
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 size={20} className="animate-spin text-[var(--brand)]" />
    </div>
  );

  if (error || !order) return (
    <div className="p-8">
      <div className="flex items-center gap-2 text-red-400 text-xs font-['Space_Mono']">
        <AlertCircle size={14} />
        <span>{error || 'Order not found'}</span>
      </div>
    </div>
  );

  const ship = order.shipping_details;
  const addr = ship?.address;
  const lineItems = order.line_items || [];

  return (
    <div className="p-6 space-y-4 max-w-4xl">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/admin/orders')} className="text-[var(--muted)] hover:text-[var(--ink)] transition-colors">
          <ArrowLeft size={16} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl text-[var(--ink)]">
            Order #{String(order.id).slice(0, 8).toUpperCase()}
          </h1>
          <p className="text-[var(--muted)] text-xs font-['Space_Mono'] mt-0.5">
            {order.created_at ? new Date(order.created_at).toLocaleString() : '—'}
          </p>
        </div>
        <button
          onClick={saveChanges}
          disabled={saving}
          className="btn-brand px-4 py-2.5 flex items-center gap-2 text-[0.65rem] disabled:opacity-60"
        >
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
          Save
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        {/* Left: order summary */}
        <div className="lg:col-span-2 space-y-4">
          {/* Line items */}
          <div className="bg-[var(--paper)] border border-[var(--line)]">
            <div className="px-5 py-3.5 border-b border-[var(--line)]">
              <h2 className="font-['Space_Mono'] text-[0.62rem] tracking-[0.16em] uppercase text-[var(--muted)]">
                Items
              </h2>
            </div>
            {lineItems.length > 0 ? (
              <div className="divide-y divide-[var(--line)]">
                {lineItems.map((item, i) => (
                  <div key={i} className="flex items-center justify-between px-5 py-3 gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-8 h-8 bg-[var(--bg)] border border-[var(--line)] flex items-center justify-center shrink-0">
                        <Package size={12} className="text-[var(--muted)]" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm text-[var(--ink)] truncate">{item.name || item.description}</p>
                        {item.size && <p className="text-[0.6rem] font-['Space_Mono'] text-[var(--muted)]">Size: {item.size}</p>}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0 text-xs font-['Space_Mono']">
                      <span className="text-[var(--muted)]">×{item.quantity || 1}</span>
                      <span className="text-[var(--ink)]">{fmtCurrency((item.price || 0) * (item.quantity || 1))}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-5 py-4">
                <p className="text-xs text-[var(--muted)] font-['Space_Mono']">
                  No line items recorded.
                </p>
              </div>
            )}
            {/* Total */}
            <div className="flex items-center justify-between px-5 py-4 border-t border-[var(--line)]">
              <span className="font-['Space_Mono'] text-[0.65rem] tracking-[0.12em] uppercase text-[var(--muted)]">Total</span>
              <span className="text-lg font-['Cormorant_Garamond'] text-[var(--ink)]">
                {fmtCurrency(order.amount_total || 0)}
              </span>
            </div>
          </div>

          {/* Fulfillment */}
          <div className="bg-[var(--paper)] border border-[var(--line)] p-5 space-y-4">
            <h2 className="font-['Space_Mono'] text-[0.62rem] tracking-[0.16em] uppercase text-[var(--muted)] border-b border-[var(--line)] pb-3">
              Fulfillment
            </h2>
            <div className="flex gap-1">
              {FULFILLMENT_STATES.map((s) => (
                <button
                  key={s}
                  onClick={() => setFulfillment(s)}
                  className={`px-3 py-1.5 text-[0.58rem] font-['Space_Mono'] tracking-[0.08em] uppercase transition-colors ${
                    fulfillment === s
                      ? 'bg-[var(--brand)] text-[#09090b]'
                      : 'border border-[var(--line)] text-[var(--muted)] hover:text-[var(--ink)]'
                  }`}
                >
                  {s}
                </button>
              ))}
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="font-['Space_Mono'] text-[0.6rem] tracking-[0.14em] uppercase text-[var(--muted)]">
                Tracking Number
              </label>
              <input
                type="text"
                value={tracking}
                onChange={(e) => setTracking(e.target.value)}
                placeholder="1Z999AA10123456784"
                className="field-surface px-3 py-2.5 text-sm w-full focus:outline-none focus:border-[var(--brand)] transition-colors"
              />
            </div>
          </div>

          {/* Notes */}
          <div className="bg-[var(--paper)] border border-[var(--line)] p-5 space-y-3">
            <h2 className="font-['Space_Mono'] text-[0.62rem] tracking-[0.16em] uppercase text-[var(--muted)] border-b border-[var(--line)] pb-3">
              Notes
            </h2>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={4}
              placeholder="Internal order notes…"
              className="field-surface px-3 py-2.5 text-sm w-full resize-none focus:outline-none focus:border-[var(--brand)] transition-colors"
            />
          </div>
        </div>

        {/* Right: customer + shipping */}
        <div className="space-y-4">
          {/* Customer */}
          <div className="bg-[var(--paper)] border border-[var(--line)] p-5 space-y-4">
            <h2 className="font-['Space_Mono'] text-[0.62rem] tracking-[0.16em] uppercase text-[var(--muted)] border-b border-[var(--line)] pb-3">
              Customer
            </h2>
            <InfoBlock label="Name">{order.customer_name || '—'}</InfoBlock>
            {order.customer_email && (
              <InfoBlock label="Email">
                <a href={`mailto:${order.customer_email}`} className="text-[var(--brand)] hover:underline">
                  {order.customer_email}
                </a>
              </InfoBlock>
            )}
            {order.customer_phone && (
              <InfoBlock label="Phone">{order.customer_phone}</InfoBlock>
            )}
          </div>

          {/* Shipping address */}
          {(ship || addr) && (
            <div className="bg-[var(--paper)] border border-[var(--line)] p-5 space-y-3">
              <h2 className="font-['Space_Mono'] text-[0.62rem] tracking-[0.16em] uppercase text-[var(--muted)] border-b border-[var(--line)] pb-3">
                Shipping Address
              </h2>
              <div className="text-sm text-[var(--ink)] space-y-0.5 font-['Space_Grotesk']">
                <p>{ship?.name || order.customer_name}</p>
                {addr?.line1 && <p>{addr.line1}</p>}
                {addr?.line2 && <p>{addr.line2}</p>}
                {addr?.city && (
                  <p>{addr.city}, {addr.state} {addr.postal_code}</p>
                )}
                {addr?.country && <p>{addr.country}</p>}
              </div>
            </div>
          )}

          {/* Payment */}
          <div className="bg-[var(--paper)] border border-[var(--line)] p-5 space-y-3">
            <h2 className="font-['Space_Mono'] text-[0.62rem] tracking-[0.16em] uppercase text-[var(--muted)] border-b border-[var(--line)] pb-3">
              Payment
            </h2>
            <InfoBlock label="Status">
              <span className={`font-['Space_Mono'] text-xs ${
                order.status === 'paid' ? 'text-emerald-400' : 'text-[var(--muted)]'
              }`}>
                {order.status}
              </span>
            </InfoBlock>
            <InfoBlock label="Amount">{fmtCurrency(order.amount_total || 0)}</InfoBlock>
            {order.stripe_session_id && (
              <InfoBlock label="Stripe Session">
                <span className="font-['Space_Mono'] text-[0.6rem] text-[var(--muted)] break-all">
                  {order.stripe_session_id}
                </span>
              </InfoBlock>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
